import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, joinRoom, listRooms, recoverIdentity, setDisplayName, type RoomListEntry } from '../lib/api.js';
import { readIdentity, updateDisplayName } from '../lib/identity.js';
import { effectiveTheme, setThemePref } from '../lib/theme.js';
import { ErrorNote, Screen } from '../components/ui.js';

const PLACEHOLDER_NAME = /^Player-\d{4}$/;

export function Home() {
  const navigate = useNavigate();
  const identity = readIdentity();
  // A shared invite link carries the room code — prefill it so joining is one tap.
  const [code, setCode] = useState(() => new URLSearchParams(window.location.search).get('code')?.toUpperCase() ?? '');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rooms, setRooms] = useState<RoomListEntry[]>([]);
  const [showRecover, setShowRecover] = useState(false);
  const [dark, setDark] = useState(() => effectiveTheme() === 'dark');

  const toggleTheme = () => {
    setThemePref(dark ? 'light' : 'dark');
    setDark(!dark);
  };

  useEffect(() => {
    listRooms()
      .then((r) => setRooms(r.rooms))
      .catch(() => setRooms([]));
  }, []);

  const join = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    setJoining(true);
    setError(null);
    try {
      const state = await joinRoom(code);
      navigate(`/room/${state.room.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join that room');
    } finally {
      setJoining(false);
    }
  };

  const needsName = !identity || PLACEHOLDER_NAME.test(identity.displayName);

  return (
    <Screen>
      <div className="home-mark">
        <button
          type="button"
          className="icon-button theme-toggle"
          onClick={toggleTheme}
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {/* U+FE0E keeps these as text glyphs instead of colour emoji on iOS. */}
          {dark ? '☀︎' : '☾︎'}
        </button>
        <span className="home-title">PANELS</span>
        <span className="home-sub">draw one, pass it on</span>
      </div>

      {needsName ? <NamePrompt /> : null}

      <form className="card" onSubmit={join}>
        <label htmlFor="code">Room code</label>
        <input
          id="code"
          className="code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABC123"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          maxLength={12}
        />
        <button type="submit" className="button primary" disabled={joining || !code.trim()}>
          {joining ? 'Joining…' : 'Join room'}
        </button>
      </form>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <button type="button" className="button" onClick={() => navigate('/create')}>
        Create a room
      </button>

      {rooms.length > 0 ? (
        <section className="section">
          <h2>Your rooms</h2>
          <ul className="list">
            {rooms.map((room) => (
              <li key={room.id}>
                <Link className="row" to={`/room/${room.id}`}>
                  <span className="row-main">
                    <strong className="mono">{room.code}</strong>
                    <span className="muted">
                      {room.memberCount} {room.memberCount === 1 ? 'player' : 'players'}
                    </span>
                  </span>
                  <span className="row-chevron">›</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <nav className="home-links">
        <Link to="/comics">Comics</Link>
        <Link to="/settings">Settings</Link>
      </nav>

      <div className="home-recover">
        {showRecover ? (
          <RecoverForm onDone={() => window.location.assign('/')} />
        ) : (
          <button type="button" className="link-button" onClick={() => setShowRecover(true)}>
            I have a recovery code
          </button>
        )}
      </div>
    </Screen>
  );
}

function NamePrompt() {
  const identity = readIdentity();
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (saved || !identity) return null;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      const result = await setDisplayName(name.trim());
      updateDisplayName(result.displayName);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that name');
    }
  };

  return (
    <form className="card card-soft" onSubmit={save}>
      <label htmlFor="name">What should we call you?</label>
      <div className="inline-form">
        <input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={identity.displayName}
          maxLength={24}
        />
        <button type="submit" className="button small" disabled={!name.trim()}>
          Save
        </button>
      </div>
      {error ? <p className="error-text">{error}</p> : null}
    </form>
  );
}

function RecoverForm({ onDone }: { onDone: () => void }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await recoverIdentity(value);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not restore that code');
      setBusy(false);
    }
  };

  return (
    <form className="card card-soft" onSubmit={submit}>
      <label htmlFor="recovery">Recovery code</label>
      <p className="muted small">Paste the code from your other device to bring your comics with you.</p>
      <textarea
        id="recovery"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={3}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />
      <button type="submit" className="button primary" disabled={busy || !value.trim()}>
        {busy ? 'Restoring…' : 'Restore my stuff'}
      </button>
      {error ? <p className="error-text">{error}</p> : null}
    </form>
  );
}

